// src/campaigns/configs/campaign-factory.ts

import { ArchetypeId } from "../../constants/archetypes";
import { CAMPAIGN_ARCHETYPE_THEMES } from "../constants/campaign-archetypes";
import { buildCampaignOG } from "../constants/campaign-og";
import { mergeCampaignCopy } from "../constants/campaign-copy";
import {
  getFourthThursdayOfNovember,
  getSecondSundayOfMay,
  getThirdSundayOfJune,
} from "../engine/campaign-date.utils";
import { CampaignCopy, RawCampaignDefinition } from "../types/campaign.types";

type CampaignTemplate = {
  slug: string;
  label: string;
  archetype: ArchetypeId;
  priority?: number;
  getEventDate: (year: number) => string;
  ogTitle: string;
  ogDescription: string;
  ogImageName: string;
  copy: Partial<CampaignCopy>;
};

function makeCampaign(year: number, template: CampaignTemplate): RawCampaignDefinition {
  return {
    id: `${template.slug}-${year}`,
    slug: template.slug,
    name: `${template.label} ${year}`,
    archetype: template.archetype,
    eventDate: template.getEventDate(year),
    priority: template.priority ?? 100,
    enabled: true,
    theme: CAMPAIGN_ARCHETYPE_THEMES[template.archetype],
    og: buildCampaignOG({
      title: template.ogTitle,
      description: template.ogDescription,
      imageName: template.ogImageName,
      year,
    }),
    copy: mergeCampaignCopy(template.copy),
  };
}

const campaignTemplates: CampaignTemplate[] = [
  {
    slug: "new-year",
    label: "New Year’s Day",
    archetype: "moments",
    getEventDate: (year) => `${year}-01-01`,
    ogTitle: "Begin the year with words that matter",
    ogDescription: "Start the year by making someone feel remembered.",
    ogImageName: "new-year",
    copy: {
      heroEyebrow: "New Year’s Day",
      heroTitle: "Begin with meaning.",
      heroTitleAccent: "before the year moves fast.",
      heroSubtitle: "Start the year by sending something they will carry with them.",
      modalTitle: "Start their year with intention.",
      modalDescription: "A new year is a beautiful reason to say what usually waits too long.",
      modalCTA: "Create a New Year Moment",
      createCTA: "Create a New Year Moment",
      createHelperText:
        "For fresh starts, gratitude, hope, and people you want to carry into the year.",
      landingOccasions: [
        "For new beginnings.",
        "For gratitude.",
        "For the people you carry forward.",
      ],
      loadingMessages: [
        "Preparing a new beginning...",
        "Choosing words for the year ahead...",
        "Making this start feel intentional...",
      ],
      recipientLoadingMessages: [
        "Someone carried you into the year.",
        "A new beginning is arriving.",
        "Open this slowly.",
      ],
      paymentHeadline: "Finish their New Year moment.",
      paymentSubtitle: "Some beginnings deserve more than a quick message.",
      shareHeadline: "Your New Year moment is ready.",
      shareSubtitle: "Send it before the year moves too fast.",
      shareMessage: "I created something meaningful for you to begin the year.",
      replyPrompt: "Begin the year by writing something back.",
      completionHeadline: "A new year found you.",
      completionSubtitle: "Someone wanted you to begin it feeling chosen.",
    },
  },
  {
    slug: "spouses-day",
    label: "Spouse’s Day",
    archetype: "heartbeat",
    getEventDate: (year) => `${year}-01-26`,
    ogTitle: "For the person you chose",
    ogDescription: "Celebrate the love that still deserves intention.",
    ogImageName: "spouses-day",
    copy: {
      heroEyebrow: "Spouse’s Day",
      heroTitle: "For the one you chose.",
      heroTitleAccent: "again and again.",
      heroSubtitle: "Create something intimate for the person who shares life with you.",
      modalTitle: "Love should still feel intentional.",
      modalDescription: "Say something deeper than routine. Make them feel chosen again.",
      modalCTA: "Create Their Moment",
      createCTA: "Create Their Moment",
      createHelperText: "For marriage, commitment, apology, appreciation, and everyday love.",
      landingOccasions: ["For your husband.", "For your wife.", "For the person you still choose."],
      recipientLoadingMessages: [
        "Someone chose you again.",
        "A private love note is arriving.",
        "Let this one land slowly.",
      ],
      paymentHeadline: "Finish their love moment.",
      paymentSubtitle: "The person beside you still deserves to feel seen.",
      shareHeadline: "Your moment is ready for them.",
      shareSubtitle: "Send it while the feeling is alive.",
      shareMessage: "I created something meaningful for you.",
      completionHeadline: "You were chosen again.",
      completionSubtitle: "Someone wanted love to feel intentional today.",
    },
  },
  {
    slug: "galentines-day",
    label: "Galentine’s Day",
    archetype: "heartbeat",
    priority: 120,
    getEventDate: (year) => `${year}-02-13`,
    ogTitle: "For the women who stayed",
    ogDescription: "Celebrate friendship, sisterhood, and the women who show up.",
    ogImageName: "galentines-day",
    copy: {
      heroEyebrow: "Galentine’s Day",
      heroTitle: "For the women who stayed.",
      heroTitleAccent: "and showed up.",
      heroSubtitle: "Create something warm for the friend, sister, or woman who made life softer.",
      modalTitle: "Friendship deserves ceremony too.",
      modalDescription: "Some women become home. Say it before the moment passes.",
      modalCTA: "Create Her Moment",
      createCTA: "Create Her Moment",
      createHelperText:
        "For best friends, sisters, girl squads, gratitude, and soft emotional appreciation.",
      landingOccasions: [
        "For your best friend.",
        "For your sister.",
        "For the woman who showed up.",
      ],
      shareMessage:
        "I created something for you because your presence has meant more than you know.",
      completionHeadline: "You are deeply appreciated.",
      completionSubtitle: "Someone wanted you to know your presence has mattered.",
    },
  },
  {
    slug: "valentines-day",
    label: "Valentine’s Day",
    archetype: "heartbeat",
    priority: 130,
    getEventDate: (year) => `${year}-02-14`,
    ogTitle: "Say what love has been waiting to hear",
    ogDescription: "Create a private Valentine’s experience for someone who matters.",
    ogImageName: "valentines-day",
    copy: {
      heroEyebrow: "Valentine’s Day",
      heroTitle: "Say what love",
      heroTitleAccent: "has been waiting to hear.",
      heroSubtitle:
        "Create a private emotional experience for the person your heart keeps choosing.",
      modalTitle: "Don’t send ordinary love today.",
      modalDescription: "Give them a moment they can open, feel, replay, and remember.",
      modalCTA: "Create a Valentine Moment",
      createCTA: "Create a Valentine Moment",
      createHelperText:
        "For partners, crushes, spouses, secret feelings, apologies, and deep love.",
      landingOccasions: ["For your love.", "For your crush.", "For the words you have delayed."],
      loadingMessages: [
        "Preparing something intimate...",
        "Choosing the heartbeat...",
        "Making love feel intentional...",
      ],
      recipientLoadingMessages: [
        "Someone’s heart chose you.",
        "A private Valentine is arriving.",
        "This was made to be felt.",
      ],
      paymentHeadline: "Finish their Valentine moment.",
      paymentSubtitle: "Today, ordinary messages disappear. This one can stay.",
      shareHeadline: "Your Valentine moment is ready.",
      shareSubtitle: "Send it before the day becomes noise.",
      shareMessage: "I made something private and meaningful for you this Valentine’s.",
      replyPrompt: "Let love answer love. Write something back.",
      completionHeadline: "You were loved out loud.",
      completionSubtitle: "Someone wanted you to feel chosen, not just texted.",
    },
  },
  {
    slug: "international-womens-day",
    label: "International Women’s Day",
    archetype: "moments",
    getEventDate: (year) => `${year}-03-08`,
    ogTitle: "Celebrate the women who carry worlds",
    ogDescription: "Recognize strength, softness, leadership, and presence.",
    ogImageName: "international-womens-day",
    copy: {
      heroEyebrow: "International Women’s Day",
      heroTitle: "For the women",
      heroTitleAccent: "who carry worlds.",
      heroSubtitle: "Create something honoring her strength, presence, grace, and becoming.",
      modalTitle: "Honor her while she can feel it.",
      modalDescription: "Recognition becomes powerful when it feels personal.",
      modalCTA: "Celebrate Her",
      createCTA: "Celebrate Her",
      createHelperText:
        "For mothers, sisters, partners, leaders, mentors, friends, and women who inspire you.",
      landingOccasions: [
        "For her strength.",
        "For her becoming.",
        "For the women who lead quietly.",
      ],
      shareMessage: "I created something to celebrate the woman you are and the impact you carry.",
      completionHeadline: "You are seen.",
      completionSubtitle: "Someone paused to honor your strength, softness, and presence.",
    },
  },
  {
    slug: "mothers-day",
    label: "Mother’s Day",
    archetype: "moments",
    priority: 130,
    getEventDate: getSecondSundayOfMay,
    ogTitle: "For the mothers who show up",
    ogDescription: "Celebrate the women who carried love quietly.",
    ogImageName: "mothers-day",
    copy: {
      heroEyebrow: "Mother’s Day",
      heroTitle: "For the mothers",
      heroTitleAccent: "who show up.",
      heroSubtitle: "Create something meaningful for the woman who carried love quietly.",
      modalTitle: "She deserves more than a forwarded message.",
      modalDescription: "Make her feel remembered, honored, and deeply appreciated.",
      modalCTA: "Create Her Mother’s Day Moment",
      createCTA: "Create Her Moment",
      createHelperText:
        "For mothers, mother figures, grandmothers, guardians, aunties, and women who raised love.",
      landingOccasions: [
        "For your mother.",
        "For your mother figure.",
        "For the woman who carried you.",
      ],
      loadingMessages: [
        "Preparing gratitude...",
        "Holding the words carefully...",
        "Making her feel remembered...",
      ],
      recipientLoadingMessages: [
        "Someone is honoring you.",
        "A grateful moment is arriving.",
        "This was made with care.",
      ],
      paymentHeadline: "Finish her Mother’s Day moment.",
      paymentSubtitle: "Some love carried you before you had words for it.",
      shareHeadline: "Her Mother’s Day moment is ready.",
      shareSubtitle: "Send it while appreciation is still close.",
      shareMessage: "I created something meaningful for you this Mother’s Day.",
      replyPrompt: "Before you leave this moment, let your heart answer back.",
      completionHeadline: "Your love was remembered.",
      completionSubtitle: "Someone paused to honor what you have carried.",
    },
  },
  {
    slug: "fathers-day",
    label: "Father’s Day",
    archetype: "safehaven",
    priority: 125,
    getEventDate: getThirdSundayOfJune,
    ogTitle: "For the fathers who became shelter",
    ogDescription: "Recognize fathers, father figures, guardians, and men who showed up.",
    ogImageName: "fathers-day",
    copy: {
      heroEyebrow: "Father’s Day",
      heroTitle: "For the fathers",
      heroTitleAccent: "who became shelter.",
      heroSubtitle: "Create something steady, honest, and meaningful for the man who showed up.",
      modalTitle: "Say the words he may never ask for.",
      modalDescription: "Strength also deserves appreciation. Make him feel seen.",
      modalCTA: "Create His Moment",
      createCTA: "Create His Moment",
      createHelperText:
        "For fathers, father figures, guardians, mentors, and men who became protection.",
      landingOccasions: ["For your father.", "For your guardian.", "For the man who showed up."],
      shareMessage: "I created something meaningful for you this Father’s Day.",
      completionHeadline: "Your presence was noticed.",
      completionSubtitle: "Someone wanted to honor the way you showed up.",
    },
  },
  {
    slug: "friendship-day",
    label: "Friendship Day",
    archetype: "safehaven",
    getEventDate: (year) => `${year}-07-30`,
    ogTitle: "For the friends who became family",
    ogDescription: "Celebrate friendship, loyalty, laughter, and the people who stayed.",
    ogImageName: "friendship-day",
    copy: {
      heroEyebrow: "Friendship Day",
      heroTitle: "For the friends",
      heroTitleAccent: "who became family.",
      heroSubtitle: "Create something personal for the person who stayed through seasons.",
      modalTitle: "Friendship deserves to be said out loud.",
      modalDescription: "Some people make life lighter just by staying.",
      modalCTA: "Create a Friendship Moment",
      createCTA: "Create a Friendship Moment",
      createHelperText:
        "For best friends, old friends, distance friendships, reconnection, and gratitude.",
      landingOccasions: [
        "For your best friend.",
        "For the one who stayed.",
        "For friendship that became home.",
      ],
      shareMessage:
        "I created something for you because your friendship has meant more than you know.",
      completionHeadline: "Your friendship mattered.",
      completionSubtitle: "Someone wanted you to know that staying meant something.",
    },
  },
  {
    slug: "world-smile-day",
    label: "World Smile Day",
    archetype: "moments",
    getEventDate: (year) => `${year}-10-02`,
    ogTitle: "Send a smile that lasts longer than a text",
    ogDescription: "Create a light, warm moment for someone who deserves joy.",
    ogImageName: "world-smile-day",
    copy: {
      heroEyebrow: "World Smile Day",
      heroTitle: "Give them a reason",
      heroTitleAccent: "to smile today.",
      heroSubtitle: "Create something light, warm, and personal for someone who deserves joy.",
      modalTitle: "A small smile can shift a whole day.",
      modalDescription: "Send something simple, beautiful, and intentionally kind.",
      modalCTA: "Create a Smile Moment",
      createCTA: "Create a Smile Moment",
      createHelperText:
        "For joy, encouragement, kindness, appreciation, and soft emotional reminders.",
      landingOccasions: ["For joy.", "For kindness.", "For someone who needs a smile."],
      shareMessage: "I made something small and meaningful to make you smile today.",
      completionHeadline: "You were thought of.",
      completionSubtitle: "Someone wanted to place a little joy in your day.",
    },
  },
  {
    slug: "world-mental-health-day",
    label: "World Mental Health Day",
    archetype: "safehaven",
    priority: 120,
    getEventDate: (year) => `${year}-10-10`,
    ogTitle: "Send care without pressure",
    ogDescription: "Create a gentle message for someone who may need softness.",
    ogImageName: "world-mental-health-day",
    copy: {
      heroEyebrow: "World Mental Health Day",
      heroTitle: "Send care",
      heroTitleAccent: "without pressure.",
      heroSubtitle: "Create a gentle, private moment for someone who may need softness today.",
      modalTitle: "Care can be quiet and still matter.",
      modalDescription: "Let them know they are not carrying everything unseen.",
      modalCTA: "Create a Gentle Moment",
      createCTA: "Create a Gentle Moment",
      createHelperText:
        "For encouragement, healing, checking in, emotional support, and soft reassurance.",
      landingOccasions: ["For healing.", "For reassurance.", "For someone carrying quietly."],
      recipientLoadingMessages: [
        "Someone is sending care.",
        "This moment has no pressure.",
        "Open it softly.",
      ],
      shareMessage: "I created something gentle for you. No pressure. Just care.",
      completionHeadline: "You are not unseen.",
      completionSubtitle: "Someone wanted care to reach you gently today.",
    },
  },
  {
    slug: "thanksgiving",
    label: "Thanksgiving",
    archetype: "moments",
    getEventDate: getFourthThursdayOfNovember,
    ogTitle: "Turn gratitude into a moment",
    ogDescription: "Create a private gratitude experience for someone who matters.",
    ogImageName: "thanksgiving",
    copy: {
      heroEyebrow: "Thanksgiving",
      heroTitle: "Gratitude should",
      heroTitleAccent: "reach the person.",
      heroSubtitle: "Create something sincere for someone whose presence has mattered.",
      modalTitle: "Don’t let gratitude stay silent.",
      modalDescription: "Make appreciation feel personal, private, and remembered.",
      modalCTA: "Create a Gratitude Moment",
      createCTA: "Create a Gratitude Moment",
      createHelperText:
        "For gratitude, family, friendship, mentors, partners, and people who shaped your year.",
      landingOccasions: [
        "For gratitude.",
        "For family.",
        "For the people who made the year softer.",
      ],
      shareMessage: "I created something to say thank you in a way that felt more intentional.",
      completionHeadline: "You were appreciated.",
      completionSubtitle: "Someone wanted gratitude to reach you properly.",
    },
  },
  {
    slug: "christmas",
    label: "Christmas",
    archetype: "moments",
    priority: 130,
    getEventDate: (year) => `${year}-12-25`,
    ogTitle: "Send a Christmas moment they can keep",
    ogDescription:
      "Create a private Christmas message for love, family, friendship, and gratitude.",
    ogImageName: "christmas",
    copy: {
      heroEyebrow: "Christmas",
      heroTitle: "Make Christmas",
      heroTitleAccent: "feel personal again.",
      heroSubtitle: "Create something warm, private, and meaningful for someone you love.",
      modalTitle: "Give them a moment, not just a message.",
      modalDescription: "Christmas is crowded with noise. Make yours feel personal.",
      modalCTA: "Create a Christmas Moment",
      createCTA: "Create a Christmas Moment",
      createHelperText:
        "For family, partners, friends, gratitude, reconnection, and people far away.",
      landingOccasions: [
        "For family.",
        "For love across distance.",
        "For the people who made the year warmer.",
      ],
      loadingMessages: [
        "Preparing Christmas warmth...",
        "Wrapping the feeling carefully...",
        "Making this moment personal...",
      ],
      recipientLoadingMessages: [
        "Someone thought of you this Christmas.",
        "A warm moment is arriving.",
        "Open it like a gift.",
      ],
      paymentHeadline: "Finish their Christmas moment.",
      paymentSubtitle: "Some gifts are opened once. Some words stay longer.",
      shareHeadline: "Your Christmas moment is ready.",
      shareSubtitle: "Send it before the day becomes too loud.",
      shareMessage: "I created something meaningful for you this Christmas.",
      completionHeadline: "You were remembered this Christmas.",
      completionSubtitle: "Someone wanted the season to reach you personally.",
    },
  },
  {
    slug: "new-years-eve",
    label: "New Year’s Eve",
    archetype: "moments",
    priority: 140,
    getEventDate: (year) => `${year}-12-31`,
    ogTitle: "End the year with words that matter",
    ogDescription: "Close the year by telling someone what they meant to you.",
    ogImageName: "new-years-eve",
    copy: {
      heroEyebrow: "New Year’s Eve",
      heroTitle: "End the year",
      heroTitleAccent: "with meaning.",
      heroSubtitle: "Create something reflective for someone who shaped your year.",
      modalTitle: "Before the year closes, say it.",
      modalDescription: "Some people should not enter your memories without knowing they mattered.",
      modalCTA: "Create an End-Year Moment",
      createCTA: "Create an End-Year Moment",
      createHelperText:
        "For reflection, gratitude, closure, appreciation, love, and people who shaped your year.",
      landingOccasions: [
        "For the year you shared.",
        "For closure.",
        "For the people who mattered.",
      ],
      shareMessage: "Before the year ends, I wanted you to receive this.",
      completionHeadline: "The year remembered you.",
      completionSubtitle: "Someone wanted you to know you mattered before the year closed.",
    },
  },
];

export function buildCampaignsForYear(year: number): RawCampaignDefinition[] {
  return campaignTemplates.map((template) => makeCampaign(year, template));
}
